import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { alertService } from '../services/alertService';
import { exportedOnText } from './stamp';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export interface PdfColumn<T = Record<string, unknown>> {
  key: keyof T;
  label: string;
  format?: (value: unknown) => string | number;
}

export interface PdfExportOptions<T = Record<string, unknown>> {
  data: T[];
  columns: PdfColumn<T>[];
  fileName?: string;
  title?: string;
}

export interface GridPdfExportOptions {
  rowData: Record<string, unknown>[];
  columnDefs: Array<{ field: string; headerName?: string }>;
  fileName?: string;
  title?: string;
}

// ─────────────────────────────────────────────
// Constants: A4 page dimensions in mm
// usable width = page width - left margin - right margin (14mm each)
// ─────────────────────────────────────────────

const PAGE = {
  PORTRAIT_WIDTH: 210,
  PORTRAIT_HEIGHT: 297,
  LANDSCAPE_WIDTH: 297,
  LANDSCAPE_HEIGHT: 210,
  MARGIN: 14,
} as const;

const PORTRAIT_USABLE = PAGE.PORTRAIT_WIDTH - PAGE.MARGIN * 2; // 182mm
const LANDSCAPE_USABLE = PAGE.LANDSCAPE_WIDTH - PAGE.MARGIN * 2; // 269mm

// mm per character at font-size 9 helvetica — calibrated to autoTable rendering
const MM_PER_CHAR = 1.85;
const CELL_PADDING = 6; // mm padding per cell (left + right)
const MIN_COL_WIDTH = 14; // mm minimum column width
// Header text renders BOLD (see headStyles below), which is measurably wider
// per character than the normal-weight data cells MM_PER_CHAR was calibrated
// against. Without this, a header's calculated "safe" width could still be a
// touch too narrow for the bold glyphs, wrapping the header to two lines.
const HEADER_WIDTH_FACTOR = 1.1;

// ─────────────────────────────────────────────
// Auto-detect: infer formatter from key name
// (mirrors excel.ts and csv.ts — same keywords)
// ─────────────────────────────────────────────

const DATE_KEYWORDS = [
  'created',
  'updated',
  'date',
  'dob',
  'birthday',
  'expiry',
  'expires',
  'modified',
  'timestamp',
];

const CURRENCY_KEYWORDS = [
  'amt',
  'amount',
  'price',
  'cost',
  'fee',
  'charge',
  'revenue',
  'salary',
];

function inferFormatter(
  key: string
): ((value: unknown) => string | number) | null {
  const lower = key.toLowerCase();
  if (DATE_KEYWORDS.some((kw) => lower.includes(kw))) return formatDate;
  if (CURRENCY_KEYWORDS.some((kw) => lower.includes(kw))) return formatCurrency;
  return null;
}

// ─────────────────────────────────────────────
// Core: calculate natural content width per column (in mm)
// Based on max of header length vs longest cell value
// ─────────────────────────────────────────────

function calcNaturalWidths<T = Record<string, unknown>>(
  data: T[],
  columns: PdfColumn<T>[]
): number[] {
  return columns.map((col) => {
    const formatter = col.format ?? inferFormatter(col.key as string);
    const headerLen = col.label.length;

    const maxDataLen = data.reduce((max, row) => {
      const raw = (row as Record<string, unknown>)[col.key as string];
      const value = formatter ? formatter(raw) : (raw ?? '');
      return Math.max(max, String(value).length);
    }, 0);

    const naturalWidth =
      Math.max(headerLen, maxDataLen) * MM_PER_CHAR + CELL_PADDING;
    return Math.max(naturalWidth, MIN_COL_WIDTH);
  });
}

// ─────────────────────────────────────────────
// Core: minimum width each column needs so its HEADER TEXT ALONE never
// wraps to a second line. Used as a floor when scaling columns down to fit
// the page — scaling must shrink the DATA-driven slack first, and must never
// push a column narrower than what its own header needs.
// ─────────────────────────────────────────────

function calcHeaderWidths<T = Record<string, unknown>>(
  columns: PdfColumn<T>[]
): number[] {
  return columns.map((col) =>
    Math.max(
      col.label.length * MM_PER_CHAR * HEADER_WIDTH_FACTOR + CELL_PADDING,
      MIN_COL_WIDTH
    )
  );
}

// ─────────────────────────────────────────────
// Core: resolve orientation BEFORE any scaling
// Uses the raw natural total width to decide
// ─────────────────────────────────────────────

function resolveOrientation(
  naturalTotalWidth: number
): 'portrait' | 'landscape' {
  return naturalTotalWidth <= PORTRAIT_USABLE ? 'portrait' : 'landscape';
}

// ─────────────────────────────────────────────
// Core: scale column widths to fit usable page width.
// Only scales down — never scales up. Each column has a HEADER FLOOR (the
// width its own header text needs to stay on one line) — scaling takes the
// shrinkage out of the data-driven slack above that floor first, instead of
// shrinking every column by the same ratio and risking a header wrap.
// ─────────────────────────────────────────────

function scaleWidths(
  naturalWidths: number[],
  headerFloors: number[],
  usableWidth: number
): number[] {
  const total = naturalWidths.reduce((sum, w) => sum + w, 0);
  if (total <= usableWidth) return naturalWidths; // fits — no scaling needed

  const floorTotal = headerFloors.reduce((sum, w) => sum + w, 0);

  // Even every column at its bare header-floor still overflows the page —
  // there are simply too many columns for one page at a readable size. There
  // is no width left to protect, so fall back to the old uniform scale (the
  // caller also drops to a smaller header font in this case so headers still
  // have the best chance of staying on one line).
  if (floorTotal >= usableWidth) {
    const ratio = usableWidth / total;
    return naturalWidths.map((w) => Math.max(w * ratio, MIN_COL_WIDTH / 2));
  }

  // Shrink only the slack ABOVE each column's header floor, distributing the
  // required reduction proportionally to how much slack each column has.
  // This way a column with a short header and long data (lots of slack)
  // gives up more width than a column whose header already defines its
  // natural width (little to no slack) — so no header is ever pushed below
  // the width it needs.
  const slack = naturalWidths.map((w, i) => Math.max(w - headerFloors[i], 0));
  const slackTotal = slack.reduce((sum, s) => sum + s, 0);
  const mustShrinkBy = total - usableWidth;

  if (slackTotal <= 0) {
    // No slack anywhere (every column is already at its floor) but the
    // total still overflows — nothing more to safely give up.
    return headerFloors.slice();
  }

  return naturalWidths.map((w, i) => {
    const give = Math.min(slack[i], (slack[i] / slackTotal) * mustShrinkBy);
    return Math.max(w - give, headerFloors[i]);
  });
}

// ─────────────────────────────────────────────
// Core: map data rows to string arrays for autoTable
// ─────────────────────────────────────────────

function buildTableRows<T = Record<string, unknown>>(
  data: T[],
  columns: PdfColumn<T>[]
): string[][] {
  return data.map((row) =>
    columns.map((col) => {
      const raw = (row as Record<string, unknown>)[col.key as string];
      const formatter = col.format ?? inferFormatter(col.key as string);
      const value = formatter ? formatter(raw) : (raw ?? '');
      return String(value);
    })
  );
}

// ─────────────────────────────────────────────
// Core: build and download the PDF
// ─────────────────────────────────────────────

function buildPdf<T = Record<string, unknown>>(
  data: T[],
  columns: PdfColumn<T>[],
  fileName: string,
  title?: string
): void {
  // Step 1 — calculate natural widths from content (no constraints)
  const naturalWidths = calcNaturalWidths(data, columns);
  const headerFloors = calcHeaderWidths(columns);
  const naturalTotal = naturalWidths.reduce((sum, w) => sum + w, 0);

  // Step 2 — resolve orientation from natural total width. A table whose
  // natural width overflows PORTRAIT switches to LANDSCAPE automatically —
  // this is also re-checked against the header floors below, since a very
  // column-heavy table can need landscape purely to keep headers readable
  // even when the data itself would have fit in portrait.
  let orientation = resolveOrientation(naturalTotal);
  let usableWidth =
    orientation === 'portrait' ? PORTRAIT_USABLE : LANDSCAPE_USABLE;

  // If even every column's bare HEADER floor doesn't fit the chosen
  // orientation, landscape buys ~48% more width — try it before giving up
  // and shrinking headers below a readable size.
  const floorTotal = headerFloors.reduce((sum, w) => sum + w, 0);
  if (orientation === 'portrait' && floorTotal > usableWidth) {
    orientation = 'landscape';
    usableWidth = LANDSCAPE_USABLE;
  }

  const pageHeight =
    orientation === 'portrait' ? PAGE.PORTRAIT_HEIGHT : PAGE.LANDSCAPE_HEIGHT;
  const pageWidth =
    orientation === 'portrait' ? PAGE.PORTRAIT_WIDTH : PAGE.LANDSCAPE_WIDTH;

  // Step 3 — scale down only if content still overflows in resolved
  // orientation. Column slack (above each header's own floor) is given up
  // first, so headers stay on one line whenever there's any slack to take.
  const colWidths = scaleWidths(naturalWidths, headerFloors, usableWidth);

  // Even landscape + every column at its header floor still overflows (an
  // extreme column count) — shrink the header font so more characters fit
  // per mm, giving headers the best remaining chance to stay on one line.
  const headerFontSize = floorTotal > usableWidth ? 7 : 9;

  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

  let startY = PAGE.MARGIN;

  // Optional title
  if (title) {
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(title, PAGE.MARGIN, startY);
    startY += 8;
  }

  // Timestamp line
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  doc.text(
    // Server-time stamp (shared helper) so it can't be spoofed by the local clock.
    exportedOnText(),
    PAGE.MARGIN,
    startY
  );
  doc.setTextColor(0);
  startY += 6;

  autoTable(doc, {
    head: [columns.map((col) => col.label)],
    body: buildTableRows(data, columns),
    startY,
    margin: { left: PAGE.MARGIN, right: PAGE.MARGIN },
    tableWidth: 'auto', // let autoTable use our explicit columnStyles widths

    // Explicit per-column widths derived from content analysis
    columnStyles: Object.fromEntries(
      colWidths.map((w, i) => [i, { cellWidth: w }])
    ),

    styles: {
      fontSize: 9,
      cellPadding: 3,
      overflow: 'linebreak',
      font: 'helvetica',
    },

    headStyles: {
      fillColor: [41, 98, 196],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: headerFontSize,
      halign: 'left',
      // Headers must stay on ONE row — columns are sized (via calcHeaderWidths
      // + scaleWidths' header-floor protection) specifically so the header
      // text fits; 'linebreak' here would still wrap in the rare case a cell
      // is forced to its absolute floor, so clip the overflow rather than
      // wrap it into a second row.
      overflow: 'hidden' as const,
      minCellHeight: 8,
    },

    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },

    // Page number footer on every page
    didDrawPage: (hookData) => {
      const pageCount = (
        doc as unknown as { internal: { getNumberOfPages: () => number } }
      ).internal.getNumberOfPages();

      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${hookData.pageNumber} of ${pageCount}`,
        pageWidth - PAGE.MARGIN,
        pageHeight - 8,
        { align: 'right' }
      );
      doc.setTextColor(0);
    },
  });

  doc.save(`${fileName}.pdf`);
}

// ─────────────────────────────────────────────
// Export: static columns or dynamic grid
// ─────────────────────────────────────────────

/**
 * Export a flat data array with a defined column map to PDF.
 * Auto-detects date/currency formatting from key names.
 * Auto-switches portrait ↔ landscape based on total content width.
 *
 * @example
 * exportToPdf({
 *   data: orders,
 *   columns: [
 *     { key: 'orderId',   label: 'Order ID'  },
 *     { key: 'createdAt', label: 'Created'   }, // auto date
 *     { key: 'totalAmt',  label: 'Total'     }, // auto currency
 *   ],
 *   fileName: 'orders-report',
 *   title:    'Orders Report',
 * });
 */
export function exportToPdf<T = Record<string, unknown>>({
  data,
  columns,
  fileName = 'export',
  title,
}: PdfExportOptions<T>): void {
  if (!data.length) {
    alertService.warn('No data to export.');
    return;
  }

  buildPdf(data, columns, fileName, title);
}

// ─────────────────────────────────────────────
// Export: raw dynamic grid (columnDefs + rowData)
// for AG Grid / TanStack or similar
// ─────────────────────────────────────────────

/**
 * Export directly from a grid's rowData + columnDefs to PDF.
 * Preserves column order and uses headerName as the PDF header.
 *
 * @example
 * exportGridToPdf({
 *   rowData:    gridRef.current.api.getRenderedNodes().map(n => n.data),
 *   columnDefs: gridRef.current.api.getColumnDefs(),
 *   fileName:   'grid-export',
 *   title:      'Grid Report',
 * });
 */
export function exportGridToPdf({
  rowData,
  columnDefs,
  fileName = 'grid-export',
  title,
}: GridPdfExportOptions): void {
  if (!rowData.length) {
    console.warn('[exportGridToPdf] No rows to export.');
    return;
  }

  const columns: PdfColumn[] = columnDefs.map((col) => ({
    key: col.field,
    label: col.headerName ?? col.field,
  }));

  buildPdf(rowData, columns, fileName, title);
}

// ─────────────────────────────────────────────
// Utility: format helpers for cell values
// (same as excel.ts and csv.ts)
// ─────────────────────────────────────────────

/** Format a JS Date or ISO string as DD/MM/YYYY */
export function formatDate(value: unknown): string {
  if (!value) return '';
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB');
}

/** Format a number as currency string e.g. "₹1,234.56" */
export function formatCurrency(value: unknown, symbol = '₹'): string {
  const num = Number(value);
  if (isNaN(num)) return '';
  return `${symbol}${num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Format a decimal as percentage string e.g. 0.25 → "25.00%" */
export function formatPercent(value: unknown, decimals = 2): string {
  const num = Number(value);
  if (isNaN(num)) return '';
  return `${(num * 100).toFixed(decimals)}%`;
}
